import { useCallback, useEffect, useRef } from 'react';

export const FALLBACK_SAMPLE_TEXT =
  "You've mentioned the chest pain started three days ago and gets worse when you lie flat. Given your family history, let's get an ECG and some bloods done this week.";

export const FALLBACK_SAMPLE_WORDS = FALLBACK_SAMPLE_TEXT.split(' ');

// Bounds the legacy V3 wait time so longer localized scripts do not stall the flow.
export const SIMULATED_TRANSCRIPTION_TOTAL_MS = 10_000;
const DEFAULT_WORD_REVEAL_INTERVAL_MS = 650;

export function useSimulatedTranscription(
  isSpeaking: boolean,
  isActive: boolean,
  onTranscriptChange: (text: string) => void,
  words: readonly string[],
  options?: { totalDurationMs?: number }
) {
  const totalDurationMs = options?.totalDurationMs;
  const wordIndexRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onChangeRef = useRef(onTranscriptChange);
  onChangeRef.current = onTranscriptChange;
  const wordsRef = useRef(words);
  wordsRef.current = words;

  const stopRevealing = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startRevealing = useCallback(() => {
    if (intervalRef.current !== null) return;

    const intervalMs =
      totalDurationMs && wordsRef.current.length
        ? totalDurationMs / wordsRef.current.length
        : DEFAULT_WORD_REVEAL_INTERVAL_MS;
    intervalRef.current = setInterval(() => {
      if (wordIndexRef.current >= wordsRef.current.length) {
        stopRevealing();
        return;
      }
      wordIndexRef.current += 1;
      const revealed = wordsRef.current.slice(0, wordIndexRef.current).join(' ');
      onChangeRef.current(revealed);
    }, intervalMs);
  }, [stopRevealing, totalDurationMs]);

  useEffect(() => {
    if (isActive && isSpeaking) {
      startRevealing();
    } else {
      stopRevealing();
    }
  }, [isSpeaking, isActive, startRevealing, stopRevealing]);

  useEffect(() => {
    return () => stopRevealing();
  }, [stopRevealing]);

  const reset = useCallback(() => {
    stopRevealing();
    wordIndexRef.current = 0;
    onChangeRef.current('');
  }, [stopRevealing]);

  return { reset };
}
